import { useQuery } from '@apollo/client';
import { Box, Grid, styled } from '@mui/material';
import { useDueDiligenceProcess } from 'components/DueDiligenceProcess/Context/DueDiligenceProcessContext';
import DueDiligenceProcessSubSectionNavigation from 'components/DueDiligenceProcess/DueDiligenceProcessSubSectionNavigation';
import { Loader } from 'components/Forms';
import { useDialog, useFeatureFlags } from 'components/hooks';
import { FeatureFlag } from 'components/hooks/useFeatureFlags';
import { ErrorState, FlexBox, PageSubTitle } from 'components/Structure';
import { ThemeButton, ThemeTypography } from 'designSystem';
import GeoDataItem from 'designSystem/DataDisplay/GeoDataItem/GeoDataItem';
import Icon from 'designSystem/Primitives/Icon/Icon';
import { GET_GEOC_PARCELS } from 'graphql/secure/queries/dueDiligence.queries';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  EDueDiligenceProcessStatus,
  ERiskLevel,
  GeoCBatch,
  GeocParcelConnection,
} from 'types/compliance.types';
import { RouteParamsWithId } from 'types/router.types';
import BatchResults from '../BatchResults';
import BatchSelection from '../BatchSelection';
import RiskAccordion from '../RiskAccordion';
import HorizontalScrollContainer from 'designSystem/Layout/HorizontalScrollContainer/HorizontalScrollContainer';
import RiskAnalysisAccordion from '../RiskAnalysisAccordion';
import BatchResultTable from '../BatchResultTable';
import PaginatedMap from 'components/FarmPlots/PaginatedMap';
import { PLOTS_PER_PAGE } from 'constants/pagination';
import { IPlot } from 'types/plot.types';
import { removeGraphConnections } from 'utils/graphql.utils';
import { GraphQlConnection } from 'types/types';
import { IPlotsFilters } from 'graphql/secure/queries/types/plot-query.types';
import { GET_PLOTS_WITH_FARMS } from 'graphql/secure/queries/plot.queries';

const DataSheetContainer = styled('div')(({ theme }) => ({
  cursor: 'pointer',
}));

const HintContainer = styled('div')(({ theme }) => ({
  background: theme.custom.themeColors.primary[5],
  borderRadius: theme.spacing(1),
  padding: theme.spacing(1),
  height: 'fit-content',
  border: `1px solid ${theme.custom.themeColors.primary[40]}`,
}));

const SuccessContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  alignItems: 'center',
  background: theme.custom.themeColors.success[20],
  borderRadius: '50px',
  padding: theme.spacing(1),
  width: 'fit-content',
}));

const RiskAnalysisResults: FC = () => {
  const { id: paramId } = useParams<RouteParamsWithId>();
  const [selectedGeoDataId, setSelectedGeoDataId] = useState<string>();
  const [selectedBatch, setSelectedBatch] = useState<GeoCBatch>();
  const { isFeatureEnabled } = useFeatureFlags();
  const disabledSectionAfterStep3 = isFeatureEnabled(FeatureFlag.DISABLE_EUDR_GUIDE_AFTER_STEP3);
  const { geoDataProcess } = useDueDiligenceProcess();
  const navigate = useNavigate();
  const { openDialog } = useDialog();

  const {
    data: plotsData,
    loading: plotsLoading,
    fetchMore: fetchMorePlots,
  } = useQuery<{ plots: GraphQlConnection<IPlot> }, { filters: IPlotsFilters; first: number }>(
    GET_PLOTS_WITH_FARMS,
    {
      variables: {
        filters: {
          ...(selectedGeoDataId && { geoDataId: selectedGeoDataId }),
        },
        first: PLOTS_PER_PAGE,
      },
      notifyOnNetworkStatusChange: true,
    }
  );

  const {
    data: parcelsData,
    loading,
    error,
  } = useQuery<{ parcels: GeocParcelConnection }>(GET_GEOC_PARCELS, {
    variables: { filters: { batchId: selectedBatch?.id, geoDataProcessId: paramId } }, // Pass all batch
    skip: !paramId || !selectedBatch?.id,
  });

  const plots: IPlot[] = useMemo(
    () => (plotsData?.plots ? removeGraphConnections(plotsData.plots) : []),
    [plotsData]
  );
  const hasNextPlotPage: boolean = !!plotsData?.plots?.pageInfo?.hasNextPage;
  const endCursorPlot: string | undefined = plotsData?.plots?.pageInfo?.endCursor;
  const plotsCount: number = plotsData?.plots?.count || 0;

  const parcels = useMemo(
    () => (parcelsData?.parcels ? removeGraphConnections(parcelsData?.parcels) : []),
    [parcelsData]
  );

  useEffect(() => {
    if (!!geoDataProcess?.geoDatas.length) {
      setSelectedGeoDataId(geoDataProcess.geoDatas[0].id);
    }
  }, [geoDataProcess]);

  useEffect(() => {
    if (
      !geoDataProcess ||
      geoDataProcess.status === EDueDiligenceProcessStatus.PENDING_RISK_ANALYSIS
    ) {
      return;
    }
    const selectedGeoData = geoDataProcess.geoDatas.find(({ id }) => id === selectedGeoDataId);
    if (selectedGeoData && selectedGeoData.geocBatches?.length) {
      setSelectedBatch(selectedGeoData.geocBatches[0]);
    }
  }, [geoDataProcess, selectedGeoDataId]);

  // TODO: Change this since we need all parcels to be present to be able to show all rsk levels correctly on the map. Risk level should be added on the plots level
  const farmParcelRisk = useMemo(
    () =>
      parcels.map(({ plot, deforestationRisk }) => ({
        farmId: plot.farmId,
        riskLevel: deforestationRisk,
      })),
    [parcels]
  );

  if (!geoDataProcess) {
    return <ErrorState />;
  }

  const handlePlotPageEndReached = async () => {
    if (endCursorPlot) {
      await fetchMorePlots({
        variables: {
          after: endCursorPlot,
        },
      });
    }
  };

  const handleClickDataSheet = (id: string) => {
    setSelectedGeoDataId(id);
  };

  const handleBatchChange = (batch: GeoCBatch) => {
    setSelectedBatch(batch);
  };

  const handleNavigateToFarm = (id: string) => {
    navigate(`/sites/${id}`);
  };

  // Analysis in progress
  if (geoDataProcess.status === EDueDiligenceProcessStatus.PENDING_RISK_ANALYSIS) {
    return (
      <DueDiligenceProcessSubSectionNavigation
        title="Deforestation risk analysis in progress"
        description="Your geo-data is now going through a detailed remote sensing deforestation risk analysis, checking satellite imagery over time and evaluating the changes in vegetation for the specified regions and plots submitted in your geo-data. "
        titleLoading
        disableNavigation
      >
        <HintContainer>
          <ThemeTypography variant="BODY_MEDIUM">
            The analysis can take up to 24 hours. We will notify you in the application and by email
            when the analysis is completed.
          </ThemeTypography>
        </HintContainer>

        <Box mt={2} mb={1}>
          <PageSubTitle title="Included geo-data" />
        </Box>

        <HorizontalScrollContainer>
          <Box display="flex" gap={2}>
            {geoDataProcess.geoDatas.map((geoData, index) => (
              <GeoDataItem
                key={index}
                title={geoData.title}
                totalCount={geoData.totalOriginalSiteCount}
                includedCount={geoData.sites?.count}
                countryCode={geoData.originCountry}
                owner={geoData.createdBy?.name}
                commodity={geoData.rawMaterial?.title}
                checked
                disabled
              />
            ))}
          </Box>
        </HorizontalScrollContainer>
      </DueDiligenceProcessSubSectionNavigation>
    );
  }

  return (
    <>
      <DueDiligenceProcessSubSectionNavigation
        disabledPreviousNavigation
        allowNextStepNavigation={!disabledSectionAfterStep3}
      >
        <Box display="flex" flexDirection="column" gap={2} mb={2}>
          <SuccessContainer>
            <Icon name="impact" color="green" />
            <ThemeTypography variant="BODY_LARGE_BOLD" color="GREEN">
              Your analysis have been saved and results can also be viewed in the compliance
              overview and partner overview.
            </ThemeTypography>
          </SuccessContainer>
          <ThemeTypography variant="BODY_MEDIUM">
            You can overview the results here for each origin geo-dataset and each plot. If you have
            a medium or high risk for any of the origin geo-datasets, you might need to perform some
            risk mitigation procedures in the next steps.
          </ThemeTypography>
        </Box>
        <RiskAnalysisAccordion />
        <FlexBox justifyContent="space-between" alignItems="flex-start" mt={2} gap={2}>
          <Box display="flex" gap={2} flexWrap="wrap">
            {geoDataProcess.geoDatas.map((geoData, index) => (
              <DataSheetContainer key={index} onClick={() => handleClickDataSheet(geoData.id)}>
                <GeoDataItem
                  title={geoData.title}
                  countryCode={geoData.originCountry}
                  owner={geoData.createdBy?.name}
                  commodity={geoData.rawMaterial?.title}
                  active={geoData?.id === selectedGeoDataId}
                  riskLevel={geoData.riskStatus ? ERiskLevel[geoData.riskStatus] : undefined}
                />
              </DataSheetContainer>
            ))}
          </Box>
          <ThemeButton
            onClick={() =>
              openDialog({
                type: 'DOWNLOAD_REPORT',
                props: {
                  geoDataProcess,
                },
              })
            }
            startIcon={<Icon name="download" size="small" />}
          >
            Download report
          </ThemeButton>
        </FlexBox>
      </DueDiligenceProcessSubSectionNavigation>

      <Box mt={2}>
        <BatchResultTable
          batches={
            geoDataProcess.geoDatas.find(({ id }) => id === selectedGeoDataId)?.geocBatches || []
          }
        />
      </Box>
      <Box my={3}>
        {selectedBatch && (
          <BatchSelection
            batches={
              geoDataProcess.geoDatas.find(({ id }) => id === selectedGeoDataId)?.geocBatches || []
            }
            selectedBatch={selectedBatch}
            onSelectBatch={handleBatchChange}
          />
        )}
      </Box>

      <Grid container spacing={2}>
        {!parcelsData ? (
          <Box position="relative" width="100%" mt={6}>
            {error && <ErrorState />}
            {loading && <Loader />}
          </Box>
        ) : (
          <>
            <Grid item xs={6}>
              <PageSubTitle title="Batch risk results" mb={1} />
              <BatchResults
                highRisks={parcelsData.parcels?.highRisks}
                mediumRisks={parcelsData.parcels?.mediumRisks}
                lowRisks={parcelsData.parcels?.lowRisks}
              />
            </Grid>
            <Grid item xs={6}>
              <PaginatedMap
                title="Batch plots overview"
                plots={plots}
                farmRiskLevels={farmParcelRisk}
                minHeight={300}
                hasNextPage={hasNextPlotPage}
                loading={plotsLoading}
                onLoadMorePlots={handlePlotPageEndReached}
                itemCount={plotsCount}
                tooltipText={`Due to browser capacity, maximum ${PLOTS_PER_PAGE} farm plots can be shown at the same time. Farm plots with warnings are prioritised first and shown on the first page. Click to load the next page of farms.`}
                mode="view"
              />
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" alignItems="center">
                <PageSubTitle title="Plot results" />
                <Box mx={1}>
                  <Icon name="farm" size="large" />
                </Box>
                <ThemeTypography variant="BODY_SMALL" color="ALMOST_BLACK">
                  {parcelsData.parcels?.count} plots
                </ThemeTypography>
              </Box>
              <Box mt={0.5} />
              {parcels.length > 0 &&
                parcels.map(parcel => (
                  <Box key={parcel.id} mb={1}>
                    <RiskAccordion
                      parcel={parcel}
                      onOpenFarmClick={() => handleNavigateToFarm(parcel.plot.farm.id)}
                    />
                  </Box>
                ))}
            </Grid>
          </>
        )}
      </Grid>
    </>
  );
};

export default RiskAnalysisResults;
